/**
 * Local Phigros skin loader (W2-01b; ARCHITECTURE.md §12, DESIGN.md D23/R11): in dev/local builds the Vite plugin
 * `phiedit-local-skin` serves the user's unzipped Phigros Note Skin (docs/research/raw/skin/, never in git) under
 * `<base>__local-skin/` and sets `import.meta.env.VITE_HAS_LOCAL_SKIN = 'true'`. This module fetches the listed
 * files and validates them exactly like Phira (formats/respack `loadRespack`); the skin store then decodes the pack.
 *
 * Public builds (`--mode public`, VITE_PUBLIC_BUILD = 'true') never contain the plugin, and the guard below is a
 * build-time constant there, so the bundler drops this loader entirely.
 */

import { loadRespack } from '../../formats/respack/load';
import type { RespackFiles, RespackValidation } from '../../formats/respack/types';
import { SkinError } from './decodeSkin';
import { LOCAL_SKIN_INDEX, LOCAL_SKIN_ROUTE, parseLocalSkinIndex } from './localSkinLayout';
import { m } from './skin.messages';

export const LOCAL_SKIN_ID = 'local-phigros';

/** Whether this build serves the local Phigros skin (constant at build time). */
export function localSkinEnabled(): boolean {
  if (import.meta.env.VITE_PUBLIC_BUILD === 'true') return false;
  return import.meta.env.VITE_HAS_LOCAL_SKIN === 'true';
}

export interface LocalSkinDeps {
  /** Default: the global fetch without the HTTP cache (the files change while developing). */
  readonly fetch?: (url: string) => Promise<Response>;
  /** Default: import.meta.env.BASE_URL. */
  readonly baseUrl?: string;
  /** Overrides VITE_HAS_LOCAL_SKIN (tests). Public builds stay disabled whatever this says. */
  readonly available?: boolean;
}

/**
 * The files of the local skin keyed by their name in the pack (info.yml, click.png, ..., click.ogg), or null when
 * this build has no local skin. Throws a SkinError (`skin.localUnreachable`) when the files cannot be fetched.
 */
export async function fetchLocalSkinFiles(deps: LocalSkinDeps = {}): Promise<ReadonlyMap<string, Uint8Array> | null> {
  if (import.meta.env.VITE_PUBLIC_BUILD === 'true') return null;
  const available = deps.available ?? import.meta.env.VITE_HAS_LOCAL_SKIN === 'true';
  if (!available) return null;
  const get =
    deps.fetch ??
    ((url: string): Promise<Response> => {
      if (typeof fetch !== 'function') return Promise.reject(new Error('fetch is not available'));
      return fetch(url, { cache: 'no-cache' });
    });
  const base = deps.baseUrl ?? import.meta.env.BASE_URL;
  const root = `${base.endsWith('/') ? base : `${base}/`}${LOCAL_SKIN_ROUTE}`;
  const unreachable = (detail: string): SkinError => new SkinError({ key: m.localUnreachable, params: { detail } });
  try {
    const res = await get(`${root}${LOCAL_SKIN_INDEX}`);
    if (!res.ok) throw unreachable(`${LOCAL_SKIN_INDEX}: HTTP ${String(res.status)}`);
    const index = parseLocalSkinIndex(await res.json());
    if (index === null) throw unreachable(`${LOCAL_SKIN_INDEX}: unexpected content`);
    const entries = await Promise.all(
      index.files.map(async (name): Promise<readonly [string, Uint8Array]> => {
        const r = await get(`${root}${encodeURIComponent(name)}`);
        if (!r.ok) throw unreachable(`${name}: HTTP ${String(r.status)}`);
        return [name, new Uint8Array(await r.arrayBuffer())];
      }),
    );
    return new Map(entries);
  } catch (e) {
    if (e instanceof SkinError) throw e;
    throw unreachable(e instanceof Error ? e.message : String(e));
  }
}

/** The local skin validated like Phira (null when this build has none). */
export async function loadLocalSkinPack(
  deps: LocalSkinDeps = {},
): Promise<{ readonly pack: RespackFiles | null; readonly validation: RespackValidation } | null> {
  const files = await fetchLocalSkinFiles(deps);
  return files === null ? null : loadRespack(files);
}
